import React from 'react';
import { useGetSearchFacetPreviewQuery, useGetSearchMetadataQuery, useGetSearchSuggestionsQuery } from '../../store/Api';
import { useGetManufacturersQuery } from '../../store/endpoints/ProductCategorizationEndpoints';
import { IQuickSearchResponse, ISearchRequest } from '../../models/Search';
import {
    aliasMatchesKey,
    IParsedProductQuery,
    normalizeForMatch,
    PSEUDONYM_ATTRIBUTES,
    quoteSugarValue,
    tokenizeProductQuery,
} from './productQueryParser';

/**
 * The product-query intelligence shared by the global palette's passthrough ('>') mode and the
 * search page's inline ProductSearchBox: taxonomy grounding, org-popularity ranking, the facet
 * preview (attribute names/values for a picked category), and inline tab completion. Both hosts
 * MUST go through these hooks — the RTK cache entries are shared by argument shape, and the
 * completion tiers are behavior the Playwright spec pins down.
 */

export type FacetMap = { [name: string]: string[] };

/** Best product-bearing taxonomy match for the typed text; undefined = do not offer. */
export const taxonomyMatch = (list: string[], typed: string): string | undefined => {
    const norm = normalizeForMatch(typed);
    if (!norm) return undefined;
    return list.find((v) => normalizeForMatch(v) === norm)
        ?? list.find((v) => normalizeForMatch(v).startsWith(norm))
        ?? list.find((v) => normalizeForMatch(v).includes(norm));
};

/**
 * The org's real taxonomy lists — unpaged, RTK-cached, filtered client-side, so "lapt" finds
 * the Laptops category the moment the lists are warm. Live-index taxonomy: everything in
 * search/metadata HAS products by construction; the curated categorization list happily names
 * categories with zero products, and an unmatched category filter strands the user on an empty
 * result page.
 */
export const useProductTaxonomy = (enabled: boolean) => {
    const knownManufacturers = useGetManufacturersQuery(undefined, { skip: !enabled });
    const searchMeta = useGetSearchMetadataQuery(undefined, { skip: !enabled });
    const validCategories = React.useMemo(() => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const cats of Object.values(searchMeta.data?.segments ?? {})) {
            for (const c of cats) {
                if (c && !seen.has(c.toLowerCase())) {
                    seen.add(c.toLowerCase());
                    out.push(c);
                }
            }
        }
        return out;
    }, [searchMeta.data]);
    const validSubCategories = React.useMemo(() => searchMeta.data?.subCategories ?? [], [searchMeta.data]);
    return { knownManufacturers, searchMeta, validCategories, validSubCategories };
};

/**
 * Org search history weights completions and pill ordering: the brand or category people
 * actually search wins the tie.
 */
export const useSearchPopularity = (enabled: boolean) => {
    const orgSuggestions = useGetSearchSuggestionsQuery(undefined, { skip: !enabled });
    const popularityOf = React.useCallback((kind: 'mfr' | 'cat', value: string): number => {
        const list = kind === 'mfr' ? orgSuggestions.data?.manufacturers : orgSuggestions.data?.categories;
        return list?.find((t) => t.value.toLowerCase() === value.toLowerCase())?.score ?? 0;
    }, [orgSuggestions.data]);
    return { orgSuggestions, popularityOf };
};

/**
 * The facet preview's criteria argument. Every subscriber (palette, section dispatcher, search
 * page box) must build its argument through this so identical terms share one RTK cache entry —
 * hand-rolled shapes split the cache and double the preview searches.
 */
export const buildFacetPreviewCriteria = (criteria: Partial<ISearchRequest>) => ({
    inStock: true,
    manufacturer: [],
    vendor: [],
    must: [],
    boost: [],
    exclude: [],
    filters: [],
    ...criteria,
});

/**
 * Attribute intelligence: prefetch the search the query would run (one item of payload) and
 * offer its facets. `facets` is gated on BOTH flags — a skipped RTK query keeps its last
 * result, so without the gate, facets from a previously-picked category keep suggesting
 * attributes after the category is removed. attributeGate additionally requires a picked
 * category/subcategory: cross-category attributes are noise.
 */
export const useFacetIntel = (opts: {
    enabled: boolean;
    attributeGate: boolean;
    catalogName?: string;
    criteria: Partial<ISearchRequest>;
}) => {
    const facetPreview = useGetSearchFacetPreviewQuery(
        { catalog: opts.catalogName, criteria: buildFacetPreviewCriteria(opts.criteria) },
        { skip: !opts.enabled }
    );
    const facets: FacetMap | undefined = opts.enabled && opts.attributeGate
        ? facetPreview.data?.metadata?.facets : undefined;
    const aliasedFacetKeys = React.useCallback((name: string): string[] => {
        // Richest attribute first: an alias like ram= should resolve to the memory attribute with
        // the most values ("Total Installed System Memory"), not whichever key happens to come
        // first ("Standard Memory" with a single value). aliasMatchesKey also drops domain
        // mismatches like "Maximum Supported System Memory" (a ceiling, not installed capacity).
        return Object.keys(facets ?? {})
            .filter((k) => aliasMatchesKey(name, k, facets?.[k]))
            .sort((a, b) => (facets?.[b]?.length ?? 0) - (facets?.[a]?.length ?? 0));
    }, [facets]);
    return { facetPreview, facets, aliasedFacetKeys };
};

/**
 * The attribute pill rows' contents: visible pills rank by what people actually filter by in
 * THIS category (search-log usage), then the facet aggregation's own document-count order;
 * the +N more picker is alphabetical for findability. Pseudonym pills (RAM, Processor, …)
 * front the row when their alias reaches real attributes and no identically-named real pill
 * is already visible.
 */
export const useAttributePills = (opts: {
    enabled: boolean;
    category?: string;
    facets?: FacetMap;
    aliasedFacetKeys: (name: string) => string[];
}) => {
    const categorySuggestions = useGetSearchSuggestionsQuery(
        { category: opts.category ?? '' },
        { skip: !opts.enabled || !opts.category }
    );
    const { category } = opts;
    const attributeUsage = React.useCallback((name: string): number => {
        if (!category) return 0;
        return categorySuggestions.data?.attributes
            ?.find((t) => t.value.toLowerCase() === name.toLowerCase())?.score ?? 0;
    }, [categorySuggestions.data, category]);
    const allFacetNames = React.useMemo(
        () => Object.keys(opts.facets ?? {}).sort((a, b) => a.localeCompare(b)),
        [opts.facets]
    );
    const facetNames = React.useMemo(
        () => [...Object.keys(opts.facets ?? {})].sort((a, b) => attributeUsage(b) - attributeUsage(a)).slice(0, 12),
        [opts.facets, attributeUsage]
    );
    const pseudonymPills = React.useMemo(
        () => PSEUDONYM_ATTRIBUTES.filter((p) =>
            !facetNames.some((n) => n.toLowerCase() === p.label.toLowerCase())
            && opts.aliasedFacetKeys(p.alias).length > 0),
        [facetNames, opts.aliasedFacetKeys]
    );
    return { categorySuggestions, attributeUsage, allFacetNames, facetNames, pseudonymPills };
};

export interface IProductCompletion {
    /** Human field name shown on the pill ("Manufacturer", or the attribute's real name). */
    field: string;
    value: string;
    /** The sugar token Tab writes ("mfr:Lenovo", '"Memory Size"="32 GB"', '"Name"='). */
    replacement: string;
    /** A space commits the replacement as a chip; empty keeps it in the input (a pending name=). */
    trailing: string;
}

/**
 * Inline tab-completion over the LAST token of the free text. Product mode only. Three tiers,
 * most specific first:
 *  1. An attribute value in progress ("Total Installed System Memory"= or "…"=32) completes
 *     against that attribute's real facet values from the preview search.
 *  2. A bare word completes against facet attribute NAMES — Tab writes `"Name"=` and value
 *     completion (tier 1) takes over.
 *  3. Otherwise the word completes against manufacturers, part numbers, then categories.
 * Accepting replaces the last token in place, so earlier tokens and their order survive.
 */
export const useProductCompletion = (opts: {
    parsed: IParsedProductQuery;
    /** The free text being typed (chips excluded). */
    query: string;
    facets?: FacetMap;
    aliasedFacetKeys: (name: string) => string[];
    quicksearchData?: IQuickSearchResponse;
    manufacturerItems?: string[];
    validCategories: string[];
    popularityOf: (kind: 'mfr' | 'cat', value: string) => number;
}): IProductCompletion | undefined => {
    const { parsed, query, facets, aliasedFacetKeys, quicksearchData, manufacturerItems, validCategories, popularityOf } = opts;
    return React.useMemo(() => {
        if (!parsed.sigil) return undefined;
        const tokens = tokenizeProductQuery(query.trim());
        const last = tokens[tokens.length - 1] ?? '';
        if (!last || /\s$/.test(query)) return undefined;

        const unquote = (v: string) => (v.length >= 2 && v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v);
        const facetKey = (name: string) =>
            Object.keys(facets ?? {}).find((k) => k.toLowerCase() === name.toLowerCase());

        // With a category picked (facets present), an "=" makes the WHOLE free text an attribute
        // pair — spaces in the name need no quotes: Total Installed System Memory=32 completes.
        const wholeAttr = query.trim().match(/^([^="]+)=("[^"]*"|[^=]*)$/);
        if (wholeAttr && wholeAttr[1].includes(' ') && facets) {
            const name = wholeAttr[1].trim();
            const partial = unquote(wholeAttr[2]);
            const key = facetKey(name)
                ?? Object.keys(facets).find((k) => k.toLowerCase().startsWith(name.toLowerCase()))
                ?? aliasedFacetKeys(name)[0];
            if (key) {
                const values = facets[key] ?? [];
                const partialNorm = normalizeForMatch(partial);
                const value = partialNorm
                    ? values.find((v) => normalizeForMatch(v).startsWith(partialNorm))
                        ?? values.find((v) => normalizeForMatch(v).includes(partialNorm))
                    : values[0];
                if (value) {
                    return {
                        field: key,
                        value,
                        replacement: `${quoteSugarValue(key)}=${quoteSugarValue(value)}`,
                        trailing: ' ',
                    };
                }
            }
            return undefined;
        }

        // Tier 1: attribute value in progress.
        const attrInProgress = last.match(/^("[^"]*"|[^\s="]+)=("[^"]*"|.*)$/);
        if (attrInProgress) {
            const name = unquote(attrInProgress[1]);
            const partial = unquote(attrInProgress[2]);
            const key = facetKey(name);
            const values = key ? (facets?.[key] ?? []) : [];
            // Equal length is still a completion: typing the exact value gets the properly
            // cased token chip on Tab.
            const value = values.find((v) => v.toLowerCase().startsWith(partial.toLowerCase()));
            if (value && key) {
                return {
                    field: key,
                    value,
                    replacement: `${quoteSugarValue(key)}=${quoteSugarValue(value)}`,
                    trailing: ' ',
                };
            }
            // Alias resolution: "ram" is not a facet name, so check the attributes it maps to for
            // a value whose normalized form matches the typed one ("32gb" → "32 GB", "i7" →
            // "Intel Core i7-13700H").
            if (!key) {
                const partialNorm = normalizeForMatch(partial);
                for (const candidateKey of aliasedFacetKeys(name)) {
                    const candidateValues = facets?.[candidateKey] ?? [];
                    const match = partialNorm
                        ? candidateValues.find((v) => normalizeForMatch(v).startsWith(partialNorm))
                            ?? candidateValues.find((v) => normalizeForMatch(v).includes(partialNorm))
                        : candidateValues[0];
                    if (match) {
                        return {
                            field: candidateKey,
                            value: match,
                            replacement: `${quoteSugarValue(candidateKey)}=${quoteSugarValue(match)}`,
                            trailing: ' ',
                        };
                    }
                }
            }
            return undefined;
        }

        if (last.length < 2) return undefined;
        const lower = last.toLowerCase();

        // Tier 2: attribute names from the facet preview (needs a category picked to exist).
        const facetName = Object.keys(facets ?? {}).find((k) => k.toLowerCase().startsWith(lower));
        if (facetName) {
            return { field: 'Attribute', value: facetName, replacement: `${quoteSugarValue(facetName)}=`, trailing: '' };
        }

        // Tier 3: field values. A category already in the query stops competing for completions.
        const sources: [string, string, string[]][] = [
            // Quicksearch manufacturers come from the live search index (term-matched, so MSI
            // appears when typed) — the curated categorization list alone is far too thin.
            ['Manufacturer', 'mfr', [
                ...(quicksearchData?.manufacturers ?? []),
                ...(manufacturerItems ?? []),
            ]],
            ['Part#', 'mpn', quicksearchData?.manufacturerPartNumbers ?? []],
            ...(parsed.criteria.category ? [] : [['Category', 'cat', validCategories] as [string, string, string[]]]),
        ];
        for (const [field, sugarKey, values] of sources) {
            const matches = values.filter((v) => v.toLowerCase().startsWith(lower));
            if (matches.length === 0) continue;
            const value = sugarKey === 'mpn' ? matches[0]
                : matches.sort((a, b) => popularityOf(sugarKey === 'cat' ? 'cat' : 'mfr', b) - popularityOf(sugarKey === 'cat' ? 'cat' : 'mfr', a))[0];
            return { field, value, replacement: `${sugarKey}:${quoteSugarValue(value)}`, trailing: ' ' };
        }
        return undefined;
    }, [parsed.sigil, parsed.criteria.category, query, facets, aliasedFacetKeys, quicksearchData, manufacturerItems, validCategories, popularityOf]);
};
